import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { getFunnelSubmissions } from "@/lib/submissions";
import { useQuery } from "@tanstack/react-query";
import type { FunnelSubmission, FormElement } from "./types";
import { useState, useMemo } from "react";
import { ArrowUpDown, Search } from "lucide-react";
import { Pagination, PaginationContent, PaginationItem, PaginationLink } from "@/components/ui/pagination";
interface SubmissionsTableProps {
  funnelId: string;
  formElements: FormElement[];
}
export const SubmissionsTable = ({
  funnelId,
  formElements
}: SubmissionsTableProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const itemsPerPage = 10;
  const {
    data: submissions,
    isLoading,
    error
  } = useQuery({
    queryKey: ["submissions", funnelId],
    queryFn: () => getFunnelSubmissions(funnelId),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: true
  });

  // Get all unique field names and their labels from submissions
  const fields = useMemo(() => {
    if (!submissions?.length) return [];
    const allFields = new Set<string>();
    submissions.forEach(submission => {
      Object.keys(submission.formData).forEach(field => allFields.add(field));
    });
    return Array.from(allFields);
  }, [submissions]);

  // Create a mapping of field IDs to titles
  const fieldTitles = useMemo(() => {
    const titleMap: Record<string, string> = {};
    formElements.forEach(element => {
      titleMap[element.id] = element.title;
    });
    return titleMap;
  }, [formElements]);

  // Filter submissions based on search term
  const filteredSubmissions = useMemo(() => {
    if (!submissions) return [];
    return submissions.filter(submission => Object.values(submission.formData).some(value => String(value).toLowerCase().includes(searchTerm.toLowerCase())));
  }, [submissions, searchTerm]);

  // Sort submissions
  const sortedSubmissions = useMemo(() => {
    if (!sortConfig) return filteredSubmissions;
    return [...filteredSubmissions].sort((a, b) => {
      const aValue = sortConfig.key === 'date' ? a.createdAt : String(a.formData[sortConfig.key] || '');
      const bValue = sortConfig.key === 'date' ? b.createdAt : String(b.formData[sortConfig.key] || '');
      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });
  }, [filteredSubmissions, sortConfig]);

  // Paginate submissions
  const paginatedSubmissions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedSubmissions.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedSubmissions, currentPage]);
  const totalPages = Math.ceil(sortedSubmissions.length / itemsPerPage);
  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current?.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  if (isLoading) {
    return <div className="text-center py-8">Loading submissions...</div>;
  }
  if (error) {
    console.error("Error fetching submissions:", error);
    return <div className="text-center py-8 text-red-500">Error loading submissions</div>;
  }
  if (!submissions || submissions.length === 0) {
    return <div className="text-center py-8 text-muted-foreground">Nincs még beküldött adat</div>;
  }
  return <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search submissions..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-8" />
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('date')} className="flex items-center gap-1">
                  Date
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
              {fields.map(field => <TableHead key={field}>
                  <Button variant="ghost" onClick={() => handleSort(field)} className="flex items-center gap-1">
                    {fieldTitles[field] || field}
                    <ArrowUpDown className="h-4 w-4" />
                  </Button>
                </TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedSubmissions.map(submission => <TableRow key={submission.id}>
                <TableCell>
                  {new Date(submission.createdAt).toLocaleDateString()}
                </TableCell>
                {fields.map(field => <TableCell key={field}>
                    {submission.formData[field] || "-"}
                  </TableCell>)}
              </TableRow>)}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && <Pagination>
          <PaginationContent>
            <PaginationItem>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="gap-1 pl-2.5">
                <span>Previous</span>
              </Button>
            </PaginationItem>
            {Array.from({
          length: totalPages
        }, (_, i) => i + 1).map(page => <PaginationItem key={page}>
                <PaginationLink onClick={() => setCurrentPage(page)} isActive={currentPage === page}>
                  {page}
                </PaginationLink>
              </PaginationItem>)}
            <PaginationItem>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="gap-1 pr-2.5">
                <span>Next</span>
              </Button>
            </PaginationItem>
          </PaginationContent>
        </Pagination>}
    </div>;
};